import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import TickerTimeout from "../../../utils/TickerTimeout";
import { PixiSettings } from "../../../pixiSettings";

// Scene transition scaling configuration - device-specific multipliers
const TRANSITION_SCALE_CONFIG = {
  DESKTOP: 0.7,
  TABLET_LANDSCAPE: 0.6,
  TABLET_PORTRAIT: 0.7,
  MOBILE_LANDSCAPE: 0.45,
  MOBILE_PORTRAIT: 0.5,
};

class SceneTransitionController {
  constructor(container, dimensions, device, orientation) {
    this.container = container;
    this.dimensions = dimensions;
    this.device = device;
    this.orientation = orientation;
    this.transitionSpine = null;
    this.isPlaying = false;
    this.midTransitionCallback = null;
    this.completeCallback = null;
    this.initialized = false;

    // Internal caches for performance optimization
    this._deviceScaleMultiplierCache = null;
    this._spinePositionCache = null;
    this._availableAnimationsCache = null; // Cache for available animations
    this._lastDevice = device;
    this._lastOrientation = orientation;
  }

  /**
   * Initialize the transition spine animation
   */
  async initializeSpine() {
    try {
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "transition_eye-skeleton";
      const atlasAlias = "transition_eye-atlas";

      // Check if assets are loaded
      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
        return false;
      }

      // Create spine instance
      this.transitionSpine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      // Verify spine was created properly
      if (
        !this.transitionSpine ||
        !this.transitionSpine.skeleton ||
        !this.transitionSpine.state
      ) {
        return false;
      }

      // Verify skeleton has required data
      if (
        !this.transitionSpine.skeleton.data ||
        !this.transitionSpine.skeleton.data.animations
      ) {
        return false;
      }

      // Add to container FIRST (before structure initialization)
      this.container.addChild(this.transitionSpine);

      // Set initial properties - CRITICAL: use 0.001 not 0
      this.transitionSpine.alpha = 0.001; // Hidden initially but in render pipeline
      this.transitionSpine.visible = true;

      // CRITICAL: Initialize spine structure to populate bones/slots/attachments
      // This compiles shaders during loading
      this.initializeSpineStructure();

      // Apply initial position and scale (only if dimensions are ready)
      if (this.dimensions) {
        this.applyScaleAndPosition();
      }

      // Invalidate animation cache when spine is initialized (spine structure may have changed)
      this._availableAnimationsCache = null;

      this.initialized = true;

      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Initialize skeleton by setting an animation to populate spine structure
   * Minimal requirement: set one animation + update once
   */
  initializeSpineStructure() {
    if (!this.transitionSpine || !this.transitionSpine.state) {
      return;
    }

    try {
      // Set any animation to force spine to load bones/slots/attachments
      const animations = this.getAvailableAnimations();
      if (animations.length > 0) {
        const firstAnimation = animations[0].name;
        this.transitionSpine.state.setAnimation(0, firstAnimation, false);
        this.transitionSpine.state.update(0.016); // Update one frame
        this.transitionSpine.skeleton.updateWorldTransform(); // Populate hierarchy
      }

      // Keep at alpha = 0.001 so PixiJS continues rendering it
      // This keeps GPU buffers hot after skeleton is populated
      this.transitionSpine.alpha = 0.001;
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Get available animations from the spine
   */
  getAvailableAnimations() {
    // Return cached result if available
    if (this._availableAnimationsCache) {
      return this._availableAnimationsCache;
    }

    const animations = this.transitionSpine?.skeleton?.data?.animations || [];

    // Cache result
    this._availableAnimationsCache = animations;

    return animations;
  }

  /**
   * Get device-specific scale multiplier (similar to WinAnimationController)
   * Cached for performance - invalidated when device/orientation changes
   */
  getDeviceScaleMultiplier() {
    // Check cache validity
    if (
      this._deviceScaleMultiplierCache !== null &&
      this._lastDevice === this.device &&
      this._lastOrientation === this.orientation
    ) {
      return this._deviceScaleMultiplierCache;
    }

    let multiplier;
    if (this.device === PixiSettings.DEVICE.MOBILE) {
      if (this.orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        multiplier = TRANSITION_SCALE_CONFIG.MOBILE_PORTRAIT;
      } else {
        multiplier = TRANSITION_SCALE_CONFIG.MOBILE_LANDSCAPE;
      }
    } else if (this.device === PixiSettings.DEVICE.TABLET) {
      if (this.orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        multiplier = TRANSITION_SCALE_CONFIG.TABLET_PORTRAIT;
      } else {
        multiplier = TRANSITION_SCALE_CONFIG.TABLET_LANDSCAPE;
      }
    } else {
      multiplier = TRANSITION_SCALE_CONFIG.DESKTOP;
    }

    // Cache result
    this._deviceScaleMultiplierCache = multiplier;
    this._lastDevice = this.device;
    this._lastOrientation = this.orientation;

    return multiplier;
  }

  /**
   * Apply scaling and positioning to match screen dimensions
   * Uses predefined device multipliers (similar to WinAnimationController)
   * Caches position calculations for performance
   */
  applyScaleAndPosition() {
    if (!this.transitionSpine || !this.transitionSpine.skeleton) {
      return;
    }

    try {
      // Get device-specific scale multiplier (cached)
      const deviceMultiplier = this.getDeviceScaleMultiplier();

      // Cache spine position to prevent recalculation
      const cacheKey = `${this.dimensions.width}_${this.dimensions.height}`;
      if (
        this._spinePositionCache?.key !== cacheKey ||
        this._spinePositionCache?.device !== this.device ||
        this._spinePositionCache?.orientation !== this.orientation
      ) {
        this._spinePositionCache = {
          key: cacheKey,
          device: this.device,
          orientation: this.orientation,
          x: this.dimensions.width / 2,
          y: this.dimensions.height / 2,
        };
      }

      // Apply scale
      this.transitionSpine.scale.set(deviceMultiplier, deviceMultiplier);

      // Center the spine on screen (using cached position)
      this.transitionSpine.position.set(
        this._spinePositionCache.x,
        this._spinePositionCache.y
      );
    } catch (error) {
      // Fallback positioning
      this.transitionSpine.scale.set(1, 1);
      if (this._spinePositionCache) {
        this.transitionSpine.position.set(
          this._spinePositionCache.x,
          this._spinePositionCache.y
        );
      } else {
        this.transitionSpine.position.set(
          this.dimensions.width / 2,
          this.dimensions.height / 2
        );
      }
    }
  }

  /**
   * Play the transition animation
   * @param {Function} midTransitionCallback - Called at the midpoint (when screen is fully covered)
   * @param {Function} completeCallback - Called when animation completes
   */
  playTransition(midTransitionCallback, completeCallback) {
    if (!this.transitionSpine || this.isPlaying) {
      return;
    }

    this.isPlaying = true;
    this.midTransitionCallback = midTransitionCallback;
    this.completeCallback = completeCallback;

    // Fade in spine from 0.001 to 1 (smooth transition, no shader recompilation)
    this.transitionSpine.alpha = 1;

    // Clear any existing listeners
    this.transitionSpine.state.clearListeners();

    // Play the transition animation (should be named "transition" or similar)
    // Check available animations
    const animations = this.getAvailableAnimations();
    const animationName = animations.length > 0 ? animations[0].name : null;

    if (!animationName) {
      this.finishTransition();
      return;
    }

    // Set the animation
    this.transitionSpine.state.setAnimation(0, animationName, false);

    // Get animation duration to calculate midpoint
    const animation =
      this.transitionSpine.skeleton.data.findAnimation(animationName);
    const duration = animation ? animation.duration : 3.3333; // Default to 3.3333s if not found

    // Schedule midpoint callback (when eye is fully closed)
    // Based on the animation structure, this should be around 50% of the duration
    const midpointTime = duration * 0.5;

    new TickerTimeout(() => {
      if (this.isPlaying && this.midTransitionCallback) {
        this.midTransitionCallback();
      }
    }, midpointTime * 1000); // Convert to milliseconds

    // Add completion listener
    this.transitionSpine.state.addListener({
      complete: (entry) => {
        if (entry.animation.name === animationName && this.isPlaying) {
          this.finishTransition();
        }
      },
    });
  }

  /**
   * Finish the transition and cleanup
   */
  finishTransition() {
    if (!this.isPlaying) {
      return;
    }

    this.isPlaying = false;

    // CRITICAL: Hide spine with 0.001 alpha (not 0) to keep in render pipeline
    if (this.transitionSpine) {
      this.transitionSpine.alpha = 0.001; // Keep GPU buffers hot
      this.transitionSpine.state.clearListeners();
      this.transitionSpine.state.clearTracks();
    }

    // Call completion callback
    if (this.completeCallback) {
      this.completeCallback();
      this.completeCallback = null;
    }

    this.midTransitionCallback = null;
  }

  /**
   * Cleanup and destroy
   */
  destroy() {
    if (this.transitionSpine) {
      this.transitionSpine.state.clearListeners();
      this.transitionSpine.state.clearTracks();
      this.transitionSpine.destroy();
      this.transitionSpine = null;
    }

    // Clear caches
    this._deviceScaleMultiplierCache = null;
    this._spinePositionCache = null;
    this._availableAnimationsCache = null;

    this.midTransitionCallback = null;
    this.completeCallback = null;
    this.isPlaying = false;
    this.initialized = false;
  }

  /**
   * Update dimensions (for window resize)
   * CRITICAL: Always update spine positioning whenever dimensions change, even when hidden
   * This ensures the spine is always positioned correctly, preventing jumps on first appearance
   */
  updateDimensions(dimensions, device, orientation) {
    // Check if dimensions or device/orientation actually changed
    const dimensionsChanged =
      !this.dimensions ||
      this.dimensions.width !== dimensions?.width ||
      this.dimensions.height !== dimensions?.height;

    const deviceChanged = this.device !== device || this.orientation !== orientation;

    if (!dimensionsChanged && !deviceChanged) {
      return;
    }

    this.dimensions = dimensions;
    this.device = device;
    this.orientation = orientation;

    // Invalidate caches when device/orientation changes
    if (deviceChanged) {
      this._deviceScaleMultiplierCache = null;
      this._spinePositionCache = null;
      this._lastDevice = device;
      this._lastOrientation = orientation;
    }

    // Invalidate position cache when dimensions change
    if (dimensionsChanged) {
      this._spinePositionCache = null;
    }

    // Note: Animation cache is only invalidated on spine initialization, not on dimension changes

    // CRITICAL: Always update spine positioning whenever dimensions change, even when hidden
    // This ensures the spine is always positioned correctly, preventing jumps on first appearance
    if (this.transitionSpine && this.dimensions) {
      this.applyScaleAndPosition();
    }
  }
}

export default SceneTransitionController;
